import { useMemo, useState } from 'react';
import type {
  UpstreamAccountRecord,
  UpstreamCreditPosition,
  UpstreamCreditTermsRecord,
} from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import { useCreditTerms, usePutCreditTerms } from '../../api/credit-terms.ts';
import { useT } from '../../i18n/index.ts';
import { Button, Checkbox, Input } from '../../ui/index.ts';
import { formatCnyCents, formatUsdCents } from '../formatters.ts';
import { StatusChip } from '../StatusChip.tsx';
import { coerceErrorMessage, useToast } from '../Toast.tsx';

/**
 * Postpaid credit terms per upstream account (#2756), shown above the
 * funding batches in the funding drawer.
 *
 * An account with no stored terms is prepaid: batches run out → the
 * capacity sync evicts it, exactly as before. Switching it to postpaid lets
 * it keep serving on the supplier's credit; the spend past the batches is
 * the payable to the supplier, and the account is evicted only once that
 * payable (net of any live batch) exceeds the CNY credit limit. An empty
 * limit is an unlimited line.
 */

interface CreditTermsPanelProps {
  readonly upstreamId: string;
  readonly accounts: readonly UpstreamAccountRecord[];
}

export function CreditTermsPanel({ upstreamId, accounts }: CreditTermsPanelProps): JSX.Element | null {
  const t = useT();
  const terms = useCreditTerms(upstreamId);

  const termsByAccount = useMemo(() => {
    const m = new Map<string, UpstreamCreditTermsRecord>();
    for (const row of terms.data ?? []) m.set(row.account_id, row);
    return m;
  }, [terms.data]);

  if (accounts.length === 0) return null;

  return (
    <section className="flex flex-col gap-3" data-testid="upstream-credit-terms">
      <div className="flex flex-col gap-1">
        <h3 className="m-0 text-label font-medium text-paper-mid">
          {t('upstream_detail.credit.title')}
        </h3>
        <p className="m-0 text-label text-paper-low">{t('upstream_detail.credit.description')}</p>
      </div>
      {terms.isError ? (
        <p className="m-0 text-body text-bad" role="alert" data-testid="upstream-credit-terms-error">
          {terms.error instanceof OpsApiError
            ? `${terms.error.code}: ${terms.error.message}`
            : t('upstream_detail.credit.error.load')}
        </p>
      ) : null}
      {terms.data !== undefined ? (
        <ul className="flex flex-col gap-3">
          {accounts.map((account) => (
            <CreditTermsRow
              key={account.id}
              upstreamId={upstreamId}
              account={account}
              terms={termsByAccount.get(account.id) ?? null}
            />
          ))}
        </ul>
      ) : null}
    </section>
  );
}

interface CreditTermsRowProps {
  readonly upstreamId: string;
  readonly account: UpstreamAccountRecord;
  readonly terms: UpstreamCreditTermsRecord | null;
}

function CreditTermsRow({ upstreamId, account, terms }: CreditTermsRowProps): JSX.Element {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const postpaid = terms?.billing_mode === 'postpaid';
  const testId = `upstream-credit-terms-${account.id}`;

  return (
    <li className="flex flex-col gap-3 rounded-lg bg-well p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-label font-medium text-paper-low">{account.label}</span>
          {postpaid ? (
            <StatusChip variant="warning" label={t('upstream_detail.credit.mode.postpaid')} testId={`${testId}-mode`} />
          ) : (
            <StatusChip variant="neutral" label={t('upstream_detail.credit.mode.prepaid')} testId={`${testId}-mode`} />
          )}
          {terms?.position?.over_limit === true ? (
            <StatusChip variant="error" label={t('upstream_detail.credit.chip.over_limit')} testId={`${testId}-over-limit`} />
          ) : null}
        </div>
        {editing ? null : (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)} data-testid={`${testId}-edit`}>
            {t('upstream_detail.credit.action.edit')}
          </Button>
        )}
      </div>

      {postpaid && terms?.position !== null && terms?.position !== undefined ? (
        <CreditPositionView position={terms.position} testId={testId} />
      ) : null}
      {postpaid && terms?.position_error !== undefined ? (
        <p className="m-0 text-label text-bad" role="alert" data-testid={`${testId}-position-error`}>
          {t('upstream_detail.credit.error.position')}
        </p>
      ) : null}

      {editing ? (
        <CreditTermsForm
          upstreamId={upstreamId}
          accountId={account.id}
          terms={terms}
          testId={testId}
          onDone={() => setEditing(false)}
        />
      ) : null}
    </li>
  );
}

function CreditPositionView({
  position,
  testId,
}: {
  readonly position: UpstreamCreditPosition;
  readonly testId: string;
}): JSX.Element {
  const t = useT();
  const unlimited = position.credit_limit_cny_cents === null;
  const pct = position.utilisation_pct === null ? 0 : Math.max(0, Math.min(100, position.utilisation_pct));
  const barClass = pct >= 95 ? 'bg-bad' : pct >= 80 ? 'bg-warn' : 'bg-ok';
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Meta
          label={t('upstream_detail.credit.meta.payable')}
          value={formatCnyCents(position.payable_cny_cents, false)}
          sub={`≈ ${formatUsdCents(position.payable_usd_cents, false)}`}
          testId={`${testId}-payable`}
        />
        <Meta
          label={t('upstream_detail.credit.meta.used')}
          value={formatCnyCents(position.credit_used_cny_cents, false)}
          testId={`${testId}-used`}
        />
        <Meta
          label={t('upstream_detail.credit.meta.limit')}
          value={unlimited
            ? t('upstream_detail.credit.unlimited')
            : formatCnyCents(position.credit_limit_cny_cents ?? 0, false)}
          testId={`${testId}-limit`}
        />
        <Meta
          label={t('upstream_detail.credit.meta.batch_remaining')}
          value={formatCnyCents(position.batch_remaining_cny_cents, false)}
          testId={`${testId}-batch-remaining`}
        />
      </div>
      {unlimited ? null : (
        <div className="flex flex-col gap-1">
          <div
            className="h-2 w-full overflow-hidden rounded-full bg-raised"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            data-testid={`${testId}-progress`}
          >
            <div className={`h-full ${barClass}`} style={{ width: `${pct}%` }} />
          </div>
          <span className="text-right font-mono text-micro text-paper-low" data-testid={`${testId}-utilisation`}>
            {t('upstream_detail.credit.utilisation', {
              pct: String(position.utilisation_pct ?? 0),
              remaining: formatCnyCents(position.credit_remaining_cny_cents ?? 0, false),
            })}
          </span>
        </div>
      )}
    </div>
  );
}

function Meta({
  label,
  value,
  sub,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub?: string;
  readonly testId: string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className="text-label text-paper-low">{label}</span>
      <span className="font-mono text-body text-paper">{value}</span>
      {sub === undefined ? null : <span className="font-mono text-micro text-paper-low">{sub}</span>}
    </div>
  );
}

/**
 * Yuan text → integer CNY cents; `undefined` = empty or invalid. An empty box
 * is NOT an unlimited line — that takes the explicit "unlimited" checkbox.
 */
export function parseCreditLimitYuan(text: string): number | undefined {
  const trimmed = text.trim();
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) return undefined;
  const cents = Math.round(Number(trimmed) * 100);
  return Number.isSafeInteger(cents) ? cents : undefined;
}

function CreditTermsForm({
  upstreamId,
  accountId,
  terms,
  testId,
  onDone,
}: {
  readonly upstreamId: string;
  readonly accountId: string;
  readonly terms: UpstreamCreditTermsRecord | null;
  readonly testId: string;
  readonly onDone: () => void;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const put = usePutCreditTerms(upstreamId);
  const [postpaid, setPostpaid] = useState(terms?.billing_mode === 'postpaid');
  const [unlimited, setUnlimited] = useState(
    terms?.billing_mode === 'postpaid' && terms.credit_limit_cny_cents === null,
  );
  const [limitYuan, setLimitYuan] = useState(
    terms?.credit_limit_cny_cents === null || terms?.credit_limit_cny_cents === undefined
      ? ''
      : String(terms.credit_limit_cny_cents / 100),
  );
  const [note, setNote] = useState(terms?.note ?? '');
  const [formError, setFormError] = useState<string | null>(null);

  function handleSave(): void {
    const limit = !postpaid || unlimited ? null : parseCreditLimitYuan(limitYuan);
    if (limit === undefined) {
      setFormError(t('upstream_detail.credit.error.limit_invalid'));
      return;
    }
    setFormError(null);
    put.mutate(
      {
        account_id: accountId,
        billing_mode: postpaid ? 'postpaid' : 'prepaid',
        credit_limit_cny_cents: limit,
        note: note.trim().length > 0 ? note.trim() : null,
      },
      {
        onSuccess: () => {
          toast.success(t('upstream_detail.credit.toast.saved'));
          onDone();
        },
        onError: (err) => toast.error(coerceErrorMessage(err)),
      },
    );
  }

  const busy = put.isPending;
  return (
    <div className="flex flex-col gap-3 border-t border-line-soft pt-3">
      <Checkbox
        checked={postpaid}
        onCheckedChange={setPostpaid}
        disabled={busy}
        label={t('upstream_detail.credit.form.postpaid')}
        testId={`${testId}-postpaid`}
      />
      <Checkbox
        checked={unlimited}
        onCheckedChange={setUnlimited}
        disabled={busy || !postpaid}
        label={t('upstream_detail.credit.form.unlimited')}
        testId={`${testId}-unlimited`}
      />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1">
          <span className="text-label text-paper-mid">{t('upstream_detail.credit.form.limit')}</span>
          <Input
            type="text"
            inputMode="decimal"
            value={limitYuan}
            onChange={(e) => setLimitYuan(e.currentTarget.value)}
            placeholder={t('upstream_detail.credit.form.limit_placeholder')}
            disabled={busy || !postpaid || unlimited}
            mono
            data-testid={`${testId}-limit-input`}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-label text-paper-mid">{t('upstream_detail.funding.form.note.label')}</span>
          <Input
            type="text"
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            disabled={busy}
            data-testid={`${testId}-note-input`}
          />
        </label>
      </div>
      {formError !== null ? (
        <p className="m-0 text-label text-bad" role="alert" data-testid={`${testId}-form-error`}>
          {formError}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button size="sm" onClick={handleSave} disabled={busy} data-testid={`${testId}-save`}>
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
